<template>
	<div class="content-tag-picker">
		<a role="button" class="right-menu-icon" title="Tags" @click.stop="togglePopover">
			<i class="fal fa-tag"></i>
		</a>
		<div v-if="isOpen" class="tag-picker-popover" @click.stop>
			<v-select
				ref="tagSelectRef"
				v-model="pickedTags"
				:options="unassignedTags"
				label="name"
				multiple
				:close-on-select="false"
				placeholder="Select or create tags"
			>
				<template #no-options="{ search }">
					<div v-if="search" class="tag-create-option">
						<span class="tag-create-label">Add "{{ search }}" as new tag</span>
						<span class="tag-create-actions">
							<i
								class="fa fa-check tag-confirm-icon"
								role="button"
								:title="`Create tag &quot;${search}&quot;`"
								@click="confirmCreateTag(search)"
							></i>
							<i
								class="fa fa-times tag-cancel-icon"
								role="button"
								title="Cancel"
								@click="cancelCreateTag"
							></i>
						</span>
					</div>
					<div v-else class="tag-create-option-empty">Type to search or create a tag</div>
				</template>
				<template #option="option">
					<span class="tag-badge">{{ option.name }}</span>
				</template>
				<template #selected-option="option">
					<span class="tag-badge">{{ option.name }}</span>
				</template>
			</v-select>
		</div>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";

export default {
	name: "ContentTagPicker",
	model: {
		prop: "isOpen",
		event: "input",
	},
	props: {
		contentId: {
			type: [String, Number],
			required: true,
		},
		entity: {
			type: String,
			default: "content",
		},
		isOpen: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			pickedTags: [],
		};
	},
	computed: {
		...mapState("tag", { allTags: "tags" }),
		...mapGetters("tag", ["tagsForContent"]),
		assignedTags() {
			return this.tagsForContent(this.contentId);
		},
		unassignedTags() {
			return this.allTags.filter(
				(tag) => !this.assignedTags.some((assignedTag) => assignedTag.id === tag.id)
			);
		},
	},
	methods: {
		...mapActions("tag", ["createTag", "createTagAssociation"]),
		togglePopover() {
			this.$emit("input", !this.isOpen);
		},
		closePopoverIfClickedOutside(event) {
			if (this.$el && !this.$el.contains(event.target)) {
				this.$emit("input", false);
			}
		},
		async confirmCreateTag(searchText) {
			let tagName = searchText.trim();
			if (!tagName) return;

			let existingTag = this.allTags.find(
				(tag) => tag.name.toLowerCase() === tagName.toLowerCase()
			);
			let tagToAssociate = existingTag || (await this.createTag(tagName));
			if (!tagToAssociate) return;
			const alreadyPicked = this.pickedTags.some( (tag) => tag.id === tagToAssociate.id ); if (!alreadyPicked) { this.pickedTags = [ ...this.pickedTags, tagToAssociate, ]; }
			this.clearSearchText();
		},
		cancelCreateTag() {
			this.clearSearchText();
		},
		clearSearchText() {
			let tagSelectInstance = this.$refs.tagSelectRef;
			if (tagSelectInstance) tagSelectInstance.search = "";
		},
		async associateTag(tag) {
			if (this.assignedTags.some((assignedTag) => assignedTag.id === tag.id)) return;
			await this.createTagAssociation({
				tagID: parseInt(tag.id),
				entity: this.entity,
				entityID: parseInt(this.contentId),
				tagName: tag.name,
			});
		},
	},
	watch: {
		pickedTags(newlyPickedTags, previouslyPickedTags) {
			let justPickedTags = newlyPickedTags.filter(
				(tag) => !previouslyPickedTags.some((previousTag) => previousTag.id === tag.id)
			);
			justPickedTags.forEach((tag) => this.associateTag(tag));
		},
		isOpen(popoverIsOpen) {
			if (popoverIsOpen) {
				this.$nextTick(() => document.addEventListener("click", this.closePopoverIfClickedOutside));
			} else {
				document.removeEventListener("click", this.closePopoverIfClickedOutside);
				this.pickedTags = [];
			}
		},
	},
	beforeDestroy() {
		document.removeEventListener("click", this.closePopoverIfClickedOutside);
	},
};
</script>

<style scoped>
.content-tag-picker {
	position: relative;
}
.content-tag-picker .right-menu-icon {
	width: 2rem;
	height: 2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 0.8px solid #cbcbcb;
	box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.07);
	border-radius: 50%;
	color: #000000;
	text-decoration: none;
}

.tag-picker-popover {
	position: absolute;
	top: 2.5rem;
	right: 0;
	width: 16rem;
	background: #fff;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	padding: 0.5rem;
	z-index: 20;
	cursor: default;
}
.tag-picker-popover >>> .vs__deselect {
	display: none;
}

.tag-picker-popover >>> .vs__dropdown-menu {
	max-height: 12rem;
	overflow-y: auto;
}

.tag-badge {
	display: inline-flex;
	align-items: center;
	padding: 0.125rem 0.625rem;
	border-radius: 1rem;
	border: 1px solid var(--primary-color, #cb222b);
	color: var(--primary-color, #cb222b);
	font-size: 0.8125rem;
	line-height: 1.5;
	background-color: transparent;
}

.tag-picker-popover >>> .vs__option .tag-badge,
.tag-picker-popover >>> .vs__dropdown-option .tag-badge {
	pointer-events: none;
}

.tag-create-option {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.5rem 0.75rem;
	gap: 0.75rem;
}

.tag-create-label {
	font-size: 0.875rem;
	color: #444;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.tag-create-actions {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-shrink: 0;
}

.tag-confirm-icon {
	color: #28a745;
	cursor: pointer;
}

.tag-cancel-icon {
	color: #dc3545;
	cursor: pointer;
}

.tag-create-option-empty {
	padding: 0.5rem 0.75rem;
	font-size: 0.875rem;
	color: #999;
}
</style>
